// src/pages/Admin/Achats/EditAchatModal.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import { getArticles } from "../../../services/articlesServices";
import { getAllUsers } from "../../../services/userServices";
import {
  updateReglement,
  createReglement,
} from "../../../services/achatsServices";

/* ---------- Helpers ---------- */
const fmtEUR = (n) => {
  const v = Number(n || 0);
  try {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v);
  } catch {
    return `${v.toFixed(2).replace(".", ",")} €`;
  }
};
const fmtDateFR = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR");
};

const STATUS = [
  "Non Réglé",
  "Réglé",
  "En Attente",
  "Annulé",
  "Remboursé",
  "Partiellement Réglé",
];
const PAYMENT_TYPES = ["En ligne", "Chèque", "Espèces", "Virement", "Chèques Vacances", "Coupons Sports", "CB", "Autre"];

export default function EditAchatModal({ isOpen, onClose, reglement, refreshAchats }) {
  const dialogRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    user_achat_id: "",
    article_id: "",
    quantite: 1,
    date_achat: "",
    message_achat: "",
    statut_reglement: "",
    type_reglement: "",
    type_achat: "",
  });

  /* ---------- ACOMPTE ---------- */
  const todayIso = new Date().toISOString().slice(0, 10);
  const [useAcompte, setUseAcompte] = useState(false);
  const [acompte, setAcompte] = useState({
    montant: "",
    date: todayIso,
    method: "Espèces",
    status: "Réglé",
    note: "",
    articleId: "",
  });

  const roundEuro = (x) => Math.round(x);

  const acompteCandidates = useMemo(() => {
    const byName = (a) => /acompte|arrhes|d[ée]p[ôo]t/i.test(a.nom || "");
    return (articles || []).filter(byName).map((a) => {
      const unit = Number(String(a.prix ?? 0).toString().replace(",", ".")) || 0;
      return { value: a.id, label: `${a.nom} (PU ${unit.toFixed(2)}€)`, unit };
    });
  }, [articles]);

  /* ---------- Effects ---------- */
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        setLoading(true);
        const [usersData, articlesData] = await Promise.all([getAllUsers(), getArticles()]);
        setUsers(usersData || []);
        setArticles(articlesData || []);
        setTimeout(() => {
          dialogRef.current?.querySelector("select, input, textarea")?.focus();
        }, 0);
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !reglement) return;
    setFormData({
      user_achat_id: reglement.user_achat_id ?? "",
      article_id: reglement.article_id ?? "",
      quantite: reglement.quantite ?? 1,
      date_achat: (reglement.date_achat || "").slice(0, 10),
      message_achat: reglement.message_achat ?? "",
      statut_reglement: reglement.statut_reglement ?? "",
      type_reglement: reglement.type_reglement ?? "",
      type_achat: reglement.type_achat ?? "",
    });
    setUseAcompte(false);
    setAcompte({
      montant: "",
      date: todayIso,
      method: "Espèces",
      status: "Réglé",
      note: "",
      articleId: "",
    });
  }, [isOpen, reglement]);

  useEffect(() => {
    setAcompte((p) => ({ ...p, articleId: p.articleId || acompteCandidates?.[0]?.value || "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles]);

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [isOpen, onClose]);

  const isLicenceAssuranceCotisation = useMemo(
    () => formData.type_achat === "licence-assurance-cotisation",
    [formData.type_achat]
  );

  const prixTotal = Number(reglement?.prix_total || 0);
  const acompteMontantNum = Number(String(acompte.montant).replace(",", ".")) || 0;

  const nouveauTotalEstime = useMemo(() => {
    if (!useAcompte || acompteMontantNum <= 0) return prixTotal;
    return Math.max(0, +(prixTotal - acompteMontantNum).toFixed(2));
  }, [useAcompte, acompteMontantNum, prixTotal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quantite") {
      const v = Math.max(1, Number(value || 1));
      setFormData((p) => ({ ...p, quantite: v }));
      return;
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  /* ---------- Submit ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLicenceAssuranceCotisation && !formData.article_id) {
      toast.warn("Veuillez sélectionner un article.");
      return;
    }

    if (useAcompte) {
      if (acompteMontantNum <= 0) return toast.warn("Montant d'acompte invalide.");
      if (acompteMontantNum > prixTotal + 0.009)
        return toast.warn("Le montant d'acompte dépasse le total du règlement.");
      if (!acompte.articleId) return toast.error("Aucun article d'acompte disponible.");
      const ref = acompteCandidates.find((c) => String(c.value) === String(acompte.articleId));
      if (!ref) return toast.error("Article d'acompte introuvable.");
      if (ref.unit === 0) return toast.error("L’article d’acompte a un prix unitaire nul.");
    }

    try {
      setLoading(true);

      // 1) Crée la ligne “Acompte”
      if (useAcompte) {
        const ref = acompteCandidates.find((c) => String(c.value) === String(acompte.articleId));
        const qty = +(acompteMontantNum / ref.unit).toFixed(4);
        await createReglement({
          user_achat_id: formData.user_achat_id,
          article_id: acompte.articleId,
          quantite: qty,
          date_achat: acompte.date,
          message_achat: acompte.note
            ? `Acompte sur règlement #${reglement?.id} — ${acompte.note}`
            : `Acompte sur règlement #${reglement?.id}`,
          statut_reglement: acompte.status || "Réglé",
          type_reglement: acompte.method || "Espèces",
          type_achat: "boutique",
        });
      }

      // 2) Met à jour le règlement courant
      const payloadUpdate = { ...formData };

      if (useAcompte && acompteMontantNum > 0) {
        payloadUpdate.prix_total = nouveauTotalEstime;
        payloadUpdate.statut_reglement = "Non Réglé";
        const appended = `Acompte de ${fmtEUR(acompteMontantNum)} en ${acompte.method} reçu le ${fmtDateFR(
          acompte.date
        )}`;
        const baseMsg = (payloadUpdate.message_achat ?? "").trim();
        payloadUpdate.message_achat = baseMsg
          ? baseMsg.includes(appended)
            ? baseMsg
            : `${baseMsg} — ${appended}`
          : appended;
      }

      await updateReglement(reglement.id, payloadUpdate);
      toast.success(useAcompte ? "Acompte enregistré et règlement mis à jour." : "Achat mis à jour avec succès.");
      onClose?.();
      await refreshAchats?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  /* ---------- UI ---------- */
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Modifier l’achat"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="relative w-full max-w-3xl flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white dark:bg-slate-900 max-h-[85vh] md:max-h-[calc(100vh-3rem)]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
          <h3 className="text-white font-semibold">Modifier l’achat</h3>
          <div className="flex items-center gap-2">
            {formData.type_achat && (
              <span className="rounded-md bg-white/15 px-2 py-0.5 text-[11px] text-white">
                {formData.type_achat === "licence-assurance-cotisation" ? "Règlement Inscription" : "Boutique"}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-1.5 text-white hover:bg-white/20"
              title="Fermer"
              aria-label="Fermer"
            >
              <FaTimes />
              <span className="hidden sm:inline">Fermer</span>
            </button>
          </div>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") e.preventDefault();
          }}
          /* ⬇⬇ IMPORTANT : on bloque l’overflow horizontal */
          className="grow overflow-y-auto overflow-x-hidden p-5 space-y-5 text-slate-800 dark:text-slate-100"
        >
          {/* Récap montants */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
            <div className="rounded-xl border px-3 py-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
              <div className="text-xs opacity-70">Total actuel</div>
              <div className="font-semibold tabular-nums">{prixTotal.toFixed(2)} €</div>
            </div>
            <div className="rounded-xl border px-3 py-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
              <div className="text-xs opacity-70">Acompte saisi</div>
              <div className="font-semibold tabular-nums">
                {useAcompte && acompteMontantNum > 0 ? `-${acompteMontantNum.toFixed(2)} €` : "—"}
              </div>
            </div>
            <div className="rounded-xl border px-3 py-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
              <div className="text-xs opacity-70">Nouveau total estimé</div>
              <div className="font-semibold tabular-nums">{nouveauTotalEstime.toFixed(2)} €</div>
            </div>
          </div>

          {/* Champs principaux (plus de min-w fixes) */}
          <div className="space-y-4">
            {/* ligne 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="min-w-0">
                <label className="block text-xs mb-1 opacity-70">Utilisateur</label>
                <select
                  name="user_achat_id"
                  value={formData.user_achat_id}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  required
                  disabled={loading}
                >
                  <option value="">Sélectionnez un utilisateur</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nom} {u.prenom}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs mb-1 opacity-70">Date d’achat</label>
                <input
                  type="date"
                  name="date_achat"
                  value={formData.date_achat}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  required
                />
              </div>
            </div>

            {/* ligne 2 */}
            {!isLicenceAssuranceCotisation && (
              <div className="min-w-0">
                <label className="block text-xs mb-1 opacity-70">Article</label>
                <select
                  name="article_id"
                  value={formData.article_id}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  required
                  disabled={loading}
                >
                  <option value="">Sélectionnez un article</option>
                  {articles.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nom}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* ligne 3 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="min-w-0">
                <label className="block text-xs mb-1 opacity-70">Quantité</label>
                <input
                  type="number"
                  name="quantite"
                  min={1}
                  value={formData.quantite}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  required
                />
              </div>

              <div className="min-w-0">
                <label className="block text-xs mb-1 opacity-70">Statut du règlement</label>
                <select
                  name="statut_reglement"
                  value={formData.statut_reglement}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  required
                >
                  <option value="">Sélectionnez un statut</option>
                  {STATUS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="min-w-0">
                <label className="block text-xs mb-1 opacity-70">Type de règlement</label>
                <select
                  name="type_reglement"
                  value={formData.type_reglement}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  required
                >
                  <option value="">Sélectionnez un type</option>
                  {PAYMENT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* message */}
            <div className="min-w-0">
              <label className="block text-xs mb-1 opacity-70">Message utilisateur</label>
              <textarea
                name="message_achat"
                value={formData.message_achat}
                onChange={handleChange}
                placeholder="Précisions, tailles, souhaits…"
                rows={4}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
              />
            </div>

            {/* ---------- BLOC ACOMPTE ---------- */}
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
              <div className="flex items-center justify-between">
                <div className="font-semibold">Acompte</div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={useAcompte}
                    onChange={(e) => setUseAcompte(e.target.checked)}
                  />
                  <span className="text-sm">Activer</span>
                </label>
              </div>

              {useAcompte && (
                <>
                  {/* ⬇⬇ min-w-0 sur le conteneur ET chaque enfant pour éviter toute poussée */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-3" style={{ minWidth: 0 }}>
                    <div className="md:col-span-2 min-w-0">
                      <label className="block text-xs mb-1 opacity-70">Article d’acompte</label>
                      <select
                        value={acompte.articleId}
                        onChange={(e) => setAcompte((p) => ({ ...p, articleId: e.target.value }))}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      >
                        {acompteCandidates.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      {acompteCandidates.length === 0 && (
                        <p className="text-xs text-rose-600 mt-1">
                          Aucun article “Acompte” trouvé. Crée un article d’acompte dans la boutique.
                        </p>
                      )}
                    </div>

                    <div className="min-w-0">
                      <label className="block text-xs mb-1 opacity-70">Montant (€)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="Ex: 50"
                        value={acompte.montant}
                        onChange={(e) => setAcompte((p) => ({ ...p, montant: e.target.value }))}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      />
                    </div>

                    <div className="min-w-0">
                      <label className="block text-xs mb-1 opacity-70">Date</label>
                      <input
                        type="date"
                        value={acompte.date}
                        onChange={(e) => setAcompte((p) => ({ ...p, date: e.target.value }))}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      />
                    </div>

                    <div className="min-w-0">
                      <label className="block text-xs mb-1 opacity-70">Type</label>
                      <select
                        value={acompte.method}
                        onChange={(e) => setAcompte((p) => ({ ...p, method: e.target.value }))}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      >
                        {["Espèces", "Chèque", "Virement", "CB", "Autre"].map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3" style={{ minWidth: 0 }}>
                    <div className="min-w-0">
                      <label className="block text-xs mb-1 opacity-70">Statut de l’acompte</label>
                      <select
                        value={acompte.status}
                        onChange={(e) => setAcompte((p) => ({ ...p, status: e.target.value }))}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      >
                        {["Réglé", "En Attente", "Annulé"].map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2 min-w-0">
                      <label className="block text-xs mb-1 opacity-70">Note (facultatif)</label>
                      <input
                        type="text"
                        placeholder="Ex: acompte rentrée / reçu par M. X"
                        value={acompte.note}
                        onChange={(e) => setAcompte((p) => ({ ...p, note: e.target.value }))}
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 -mx-5 px-5 pb-4 pt-3 bg-white/90 dark:bg-slate-900/90 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-slate-900/60 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              type="button"
              onClick={onClose}
              className="!rounded-xl gap-2"
            />
            <Button
              text="Enregistrer"
              icon={FaSave}
              type="submit"
              className="!rounded-xl gap-2"
              disabled={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
